// Phase 5 (api-phase5.md "Talk-down library"): the messages operators and the agent play on site speakers.
//   global   Setup → Talk-down library (#/setup/talkdown): siteId null, every site sees them.
//   site     Setup → site → Talk-down: the site's own messages, offered before the global ones.
// Supervisors read; admins create, edit and disable (PATCH active). Each message: name, category, language (EN / ES),
// text with {site} {camera} {zone} {time}, whether the agent may play it, sort order, and a Preview of the speech.

import { useState } from 'react';
import { api, errorText, TALKDOWN_CATEGORIES, type TalkdownCategory, type TalkdownLanguage, type TalkdownMessage, type TalkdownMessageBody } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { ErrorBox, InlineError, Loading, Pill } from '../../components/ui.tsx';
import { categoryLabel } from '../../console/devices.ts';
import { useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { Toggle } from './common.tsx';

type Draft = { name: string; category: TalkdownCategory; language: TalkdownLanguage; text: string; agentAllowed: boolean; sortOrder: number };

const blank = (): Draft => ({ name: '', category: 'warning', language: 'en', text: '', agentAllowed: true, sortOrder: 100 });
const draftOf = (m: TalkdownMessage): Draft => ({
  name: m.name,
  category: (TALKDOWN_CATEGORIES as readonly string[]).includes(m.category) ? (m.category as TalkdownCategory) : 'custom',
  language: m.language === 'es' ? 'es' : 'en',
  text: m.text,
  agentAllowed: m.agentAllowed,
  sortOrder: m.sortOrder,
});

/** Plays the server's speech for a text (POST /api/talkdown/preview). */
export function PreviewButton({ text, language, small = true }: { text: string; language: string; small?: boolean }) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [audio] = useState(() => (typeof Audio === 'undefined' ? null : new Audio()));
  const go = async () => {
    if (!audio) return;
    if (state === 'playing') {
      audio.pause();
      setState('idle');
      return;
    }
    setErr(null);
    setState('loading');
    try {
      audio.src = await api.talkdownPreview(text, language);
      audio.onended = () => setState('idle');
      await audio.play();
      setState('playing');
    } catch (e) {
      setErr(errorText(e));
      setState('idle');
    }
  };
  return (
    <span className="row-wrap nowrap">
      <button type="button" className={`btn${small ? ' sm' : ''}`} onClick={() => void go()} disabled={!text.trim() || !audio} title="Listen to the speech (the server's voice; {zone} and the like are read as written here)">
        {state === 'loading' ? <span className="spinner" aria-hidden="true" /> : <Icon name={state === 'playing' ? 'pause' : 'speaker'} />}
        {state === 'playing' ? 'Stop' : 'Preview'}
      </button>
      {err && <span className="note bad-t">{err}</span>}
    </span>
  );
}

function MessageForm({ d, setD, disabled }: { d: Draft; setD: (d: Draft) => void; disabled: boolean }) {
  return (
    <fieldset disabled={disabled} className="tdform">
      <div className="fgrid">
        <label className="field">
          <span className="lbl">Name</span>
          <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} maxLength={100} placeholder="e.g. Leave the property" />
        </label>
        <label className="field">
          <span className="lbl">Category</span>
          <select value={d.category} onChange={(e) => setD({ ...d, category: e.target.value as TalkdownCategory })}>
            {TALKDOWN_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Language</span>
          <select value={d.language} onChange={(e) => setD({ ...d, language: e.target.value === 'es' ? 'es' : 'en' })}>
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </select>
        </label>
        <label className="field">
          <span className="lbl">Order</span>
          <input type="number" min={0} value={d.sortOrder} onChange={(e) => setD({ ...d, sortOrder: Number(e.target.value) || 0 })} />
        </label>
      </div>
      <label className="field">
        <span className="lbl">Text · {'{site} {camera} {zone} {time}'} are filled in when it plays</span>
        <textarea rows={2} maxLength={400} value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} placeholder="You, near {zone}. You are on camera. Leave the property now." />
      </label>
      <Toggle checked={d.agentAllowed} onChange={(v) => setD({ ...d, agentAllowed: v })} label="The AI agent may play it (Level 2+ sites)" disabled={disabled} />
    </fieldset>
  );
}

function Row({ m, canEdit, onSaved }: { m: TalkdownMessage; canEdit: boolean; onSaved: (m: TalkdownMessage) => void }) {
  const [edit, setEdit] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const save = async (patch: Partial<TalkdownMessageBody> & { active?: boolean }) => {
    setBusy(true);
    setErr(null);
    try {
      onSaved(await api.patchTalkdownMessage(m.id, patch));
      setEdit(null);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`tdmsg${m.active ? '' : ' off'}`}>
      <div className="row-wrap">
        <b>{m.name}</b>
        <span className="pill tiny-pill">{categoryLabel(m.category)}</span>
        <span className="pill tiny-pill">{m.language === 'es' ? 'Español' : 'English'}</span>
        {m.agentAllowed ? <Pill tone="ai">AI may play</Pill> : <span className="pill tiny-pill">operators only</span>}
        {!m.active && <Pill tone="warn">disabled</Pill>}
        <span className="mono dim tiny">#{m.sortOrder} · v{m.version}</span>
        <span className="sp" />
        <PreviewButton text={m.text} language={m.language} />
        {canEdit && !edit && (
          <>
            <button type="button" className="btn sm" onClick={() => setEdit(draftOf(m))}>
              Edit
            </button>
            <button type="button" className="btn sm" onClick={() => void save({ active: !m.active })} disabled={busy}>
              {m.active ? 'Disable' : 'Enable'}
            </button>
          </>
        )}
      </div>
      {!edit && <div className="small">{m.text}</div>}
      {edit && (
        <>
          <MessageForm d={edit} setD={setEdit} disabled={busy} />
          <div className="savebar">
            <button
              type="button"
              className="btn pri"
              disabled={busy || !edit.name.trim() || !edit.text.trim()}
              onClick={() => void save({ name: edit.name.trim(), category: edit.category, language: edit.language, text: edit.text.trim(), agentAllowed: edit.agentAllowed, sortOrder: edit.sortOrder })}
            >
              <Icon name="check" />
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn" onClick={() => setEdit(null)} disabled={busy}>
              Cancel
            </button>
            <PreviewButton text={edit.text} language={edit.language} small={false} />
          </div>
        </>
      )}
      <InlineError error={err} />
    </div>
  );
}

/** The library for one scope: siteId null = global. */
export function TalkdownLibrary({ siteId, siteName }: { siteId: string | null; siteName?: string }) {
  const s = useSession();
  const canEdit = s.isAdmin;
  const load = useLoad((sig) => api.talkdownMessages(siteId, sig), [siteId]);
  const [adding, setAdding] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [lang, setLang] = useState<'all' | 'en' | 'es'>('all');
  const list = (load.data ?? []).filter((m) => lang === 'all' || m.language === lang).slice().sort((a, b) => Number(b.active) - Number(a.active) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  const put = (m: TalkdownMessage) => load.setData((l) => (l ? (l.some((x) => x.id === m.id) ? l.map((x) => (x.id === m.id ? m : x)) : [...l, m]) : [m]));
  const create = async () => {
    if (!adding) return;
    setBusy(true);
    setErr(null);
    try {
      put(await api.createTalkdownMessage({ siteId, name: adding.name.trim(), category: adding.category, language: adding.language, text: adding.text.trim(), agentAllowed: adding.agentAllowed, sortOrder: adding.sortOrder }));
      setAdding(null);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="tabbody">
      <div className="banner">
        <Icon name="speaker" />
        <span>
          {siteId
            ? `Messages only ${siteName ?? 'this site'} uses. Operators see them first, then the global library.`
            : 'The global library: every site can play these. Sites can add their own (Setup → site → Talk-down).'}{' '}
          Speech is rendered on the server and cached, so a message always sounds the same.
        </span>
      </div>
      <div className="row-wrap">
        <div className="seg" role="radiogroup" aria-label="Language">
          {(['all', 'en', 'es'] as const).map((l) => (
            <button key={l} type="button" role="radio" aria-checked={lang === l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>
              {l === 'all' ? 'All' : l === 'en' ? 'English' : 'Español'}
            </button>
          ))}
        </div>
        <span className="sp" />
        {canEdit && !adding && (
          <button type="button" className="btn" onClick={() => setAdding(blank())}>
            <Icon name="plus" />
            New message
          </button>
        )}
        {!canEdit && (
          <span className="note">
            <Icon name="lock" /> Admins edit the library.
          </span>
        )}
      </div>
      {adding && (
        <div className="tdmsg new">
          <b>New {siteId ? 'site' : 'global'} message</b>
          <MessageForm d={adding} setD={setAdding} disabled={busy} />
          <div className="savebar">
            <button type="button" className="btn pri" onClick={() => void create()} disabled={busy || !adding.name.trim() || !adding.text.trim()}>
              <Icon name="check" />
              {busy ? 'Adding…' : 'Add message'}
            </button>
            <button type="button" className="btn" onClick={() => setAdding(null)} disabled={busy}>
              Cancel
            </button>
            <PreviewButton text={adding.text} language={adding.language} small={false} />
          </div>
          <InlineError error={err} />
        </div>
      )}
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the talk-down library" />
      ) : !load.data ? (
        <Loading label="Loading messages…" />
      ) : list.length === 0 ? (
        <div className="note">{siteId ? 'No site messages. The global library applies.' : 'No messages yet.'}</div>
      ) : (
        list.map((m) => <Row key={m.id} m={m} canEdit={canEdit} onSaved={put} />)
      )}
    </div>
  );
}

/** Setup → Talk-down library (global). */
export function TalkdownEditor() {
  return (
    <>
      <div className="page-h">
        <div>
          <h2>
            <Icon name="speaker" />
            Talk-down library
          </h2>
          <p>What operators and the AI agent can play on site speakers. Site messages are under each site.</p>
        </div>
      </div>
      <section className="card2">
        <TalkdownLibrary siteId={null} />
      </section>
    </>
  );
}
